import { Component, signal, inject, input, output, effect } from '@angular/core';
import { formatDateTime } from '../../core/utils/date.util';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { takeUntil, Subject } from 'rxjs';
import { SelfService, SelfProfile } from '../../core/services/self.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-edit-account',
  standalone: true,
  imports: [FormsModule, NgClass],
  templateUrl: './edit-account.component.html',
  styleUrl: './edit-account.component.scss',
})
export class EditAccountComponent {
  private selfService = inject(SelfService);
  private toast = inject(ToastService);
  private destroy$ = new Subject<void>();

  readonly open = input(false);
  readonly closed = output<void>();

  profile = signal<SelfProfile | null>(null);
  loading = signal(false);
  editing = signal(false);
  saving = signal(false);

  editDisplayName = '';
  editEmail = '';

  showPasswordForm = signal(false);
  currentPassword = '';
  newPassword = '';
  confirmPassword = '';
  changingPassword = signal(false);

  /** Load profile whenever drawer opens */
  private openEffect = effect(() => {
    if (this.open()) {
      this.loadProfile();
    } else {
      // Reset state when closing
      this.editing.set(false);
      this.showPasswordForm.set(false);
      this.resetFormFields();
    }
  });

  close(): void {
    this.closed.emit();
  }

  // ── Profile ──

  loadProfile(): void {
    this.loading.set(true);
    this.selfService
      .getProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (p) => {
          this.profile.set(p);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          const message = err?.error?.message ?? err?.message ?? 'Failed to load profile';
          this.toast.error('Error', message);
        },
      });
  }

  startEdit(): void {
    const p = this.profile();
    if (!p) return;
    this.editDisplayName = p.displayName ?? '';
    this.editEmail = p.email ?? '';
    this.editing.set(true);
  }

  cancelEdit(): void {
    this.editing.set(false);
  }

  saveProfile(): void {
    this.saving.set(true);
    this.selfService
      .updateProfile({
        displayName: this.editDisplayName !== undefined ? (this.editDisplayName || null) : undefined,
        email: this.editEmail !== undefined ? (this.editEmail || null) : undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.profile.set(updated);
          this.editing.set(false);
          this.saving.set(false);
          this.toast.success('Success', 'Profile updated successfully');
        },
        error: (err) => {
          this.saving.set(false);
          const message = err?.error?.message ?? err?.message ?? 'Failed to update profile';
          this.toast.error('Error', message);
        },
      });
  }

  // ── Change Password ──

  cancelPasswordChange(): void {
    this.showPasswordForm.set(false);
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }

  submitPasswordChange(): void {
    if (!this.currentPassword || !this.newPassword) {
      this.toast.error('Validation', 'Please fill in all password fields');
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.toast.error('Validation', 'New password and confirmation do not match');
      return;
    }
    if (this.newPassword.length < 8) {
      this.toast.error('Validation', 'New password must be at least 8 characters');
      return;
    }

    this.changingPassword.set(true);
    this.selfService
      .changePassword(this.currentPassword, this.newPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.changingPassword.set(false);
          this.showPasswordForm.set(false);
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.toast.success('Success', 'Password changed successfully');
        },
        error: (err) => {
          this.changingPassword.set(false);
          const message = err?.error?.message ?? err?.message ?? 'Failed to change password';
          this.toast.error('Error', message);
        },
      });
  }

  // ── Helpers ──

  statusClass(status: string): Record<string, boolean> {
    const v = (status ?? '').toLowerCase();
    return {
      'status-pill': true,
      'status-active': v === 'active',
      'status-disabled': v === 'inactive',
    };
  }

  /** Shared formatter (see core/utils/date.util); this page reads unset as 'Never'. */
  readonly formatDate = (dt: string | null): string => formatDateTime(dt, 'Never');

  private resetFormFields(): void {
    this.editDisplayName = '';
    this.editEmail = '';
    this.currentPassword = '';
    this.newPassword = '';
    this.confirmPassword = '';
  }
}
